import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FLOSS, flossByCode, luminance } from '../data/dmc';
import { interpretHeuristically } from '../brief/heuristic';
import { interpretPhoto, type PhotoPalette } from '../brief/image';
import { interpretWithGemini, GeminiError, hasGeminiKey } from '../brief/gemini';
import { encodeBrief } from '../brief/request';
import { EASE_PRESETS, WRIST_PRESETS, type DesignBrief } from '../brief/types';
import { randomSeed } from '../engine/rng';
import { navigate } from './router';
import type { Settings } from '../state/storage';
import type { BraceletStyle, Sizing } from '../engine/types';

interface Props {
  settings: Settings;
  onSettings: (s: Settings) => void;
}

const STYLE_CHOICES: { id: BraceletStyle | 'any'; label: string; hint: string }[] = [
  { id: 'any', label: 'Surprise me', hint: 'A spread across all three kinds' },
  { id: 'normal', label: 'Chevrons & stripes', hint: 'Diagonal knots, the classic kind' },
  { id: 'alpha', label: 'Words & pictures', hint: 'Alpha: one knot per pixel' },
  { id: 'chain', label: 'Daisy / fruit chain', hint: 'Motifs joined by lengths of chain' },
];

export function DescribeScreen({ settings, onSettings }: Props) {
  const [text, setText] = useState('');
  const [pinned, setPinned] = useState<string[]>([]);
  const [style, setStyle] = useState<BraceletStyle | 'any'>('any');
  const [seed, setSeed] = useState(() => randomSeed());
  const [sizing, setSizing] = useState<Sizing>(settings.sizing);
  const [photo, setPhoto] = useState<{ palette: PhotoPalette; dataUrl: string; brief: DesignBrief } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [useAi, setUseAi] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const keyPresent = hasGeminiKey(settings.geminiKey);
  useEffect(() => {
    if (!keyPresent) setUseAi(false);
  }, [keyPresent]);

  // Live preview of what the deterministic interpreter makes of her words. This
  // updates as she types and never touches the network.
  const preview = useMemo(
    () =>
      interpretHeuristically(text, {
        sizing,
        pinnedColors: pinned,
        stylePreference: style,
      }),
    [text, pinned, style, sizing],
  );

  const effectiveColors = photo && pinned.length === 0 ? photo.brief.colors : preview.colors;

  const togglePinned = useCallback((code: string) => {
    setPinned((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : prev.length >= 6 ? prev : [...prev, code],
    );
  }, []);

  const onPhoto = useCallback(
    async (file: File) => {
      setError(null);
      setBusy('Reading the photo…');
      try {
        const result = await interpretPhoto(file, { sizing, text });
        setPhoto(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not read that photo.');
      } finally {
        setBusy(null);
      }
    },
    [sizing, text],
  );

  const go = useCallback(
    async (withAi: boolean) => {
      setError(null);
      let brief: DesignBrief;

      if (withAi && keyPresent) {
        setBusy('Asking Gemini what this should look like…');
        try {
          brief = await interpretWithGemini({
            apiKey: settings.geminiKey,
            model: settings.geminiModel,
            text: text || 'a friendship bracelet',
            sizing,
            imageDataUrl: photo?.dataUrl,
          });
          if (pinned.length) brief = { ...brief, colors: dedupe([...pinned, ...brief.colors]).slice(0, 5) };
          if (style !== 'any') brief = { ...brief, stylePreference: style };
        } catch (err) {
          const message =
            err instanceof GeminiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Gemini could not be reached.';
          setError(`${message} Carrying on without AI.`);
          brief = fallbackBrief();
        } finally {
          setBusy(null);
        }
      } else {
        brief = fallbackBrief();
      }

      onSettings({ ...settings, sizing });
      navigate({ name: 'options', brief: encodeBrief(brief), seed });

      function fallbackBrief(): DesignBrief {
        if (photo) {
          const merged = {
            ...photo.brief,
            sizing,
            colors: dedupe([...pinned, ...photo.brief.colors]).slice(0, 5),
            stylePreference: style,
            text: text || photo.brief.text,
          };
          // Words she typed still steer motifs and any word to spell.
          const words = interpretHeuristically(text, { sizing, stylePreference: style });
          return {
            ...merged,
            word: words.word,
            motifs: words.motifs.length ? words.motifs : merged.motifs,
            mood: words.matchedTheme ? words.mood : merged.mood,
            sourceNote: `${photo.brief.sourceNote}${text ? ' Your words were used for the shape and any lettering.' : ''}`,
          };
        }
        return preview;
      }
    },
    [keyPresent, onSettings, photo, pinned, preview, seed, settings, sizing, style, text],
  );

  return (
    <div className="screen">
      <section className="card">
        <h1>Turn an idea into a bracelet you can actually knot</h1>
        <p className="muted small">
          Describe it, or start from a photo. Knotwright works out the exact knots, the setup order,
          and how long to cut every single string — with the arithmetic shown, so you can check it
          before you cut.
        </p>
      </section>

      <section className="card">
        <h2>What do you want to make?</h2>
        <label className="field">
          <span className="field-label">Describe it in your own words</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="something like a sunset&#10;cherries on cream&#10;a bracelet that says BFF"
            rows={3}
            autoComplete="off"
          />
        </label>

        <div className="row">
          <button type="button" className="secondary" onClick={() => fileRef.current?.click()}>
            {photo ? 'Use a different photo' : 'Use a photo instead'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onPhoto(f);
              e.target.value = '';
            }}
          />
          {photo && (
            <button type="button" className="link" onClick={() => setPhoto(null)}>
              Remove photo
            </button>
          )}
        </div>

        {photo && (
          <div className="photo-result">
            <img src={photo.dataUrl} alt="The photo you chose" />
            <div>
              <p className="muted small">{photo.brief.sourceNote}</p>
              <ul className="palette palette--compact">
                {photo.palette.swatches.map((s, i) => (
                  <li key={i}>
                    <span className="swatch" style={{ background: s.floss.hex }} />
                    <span className="swatch-label">
                      <strong>{s.floss.dmc}</strong>
                      <em>{Math.round(s.share * 100)}%</em>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Colours</h2>
        <p className="muted small">
          {pinned.length > 0
            ? `Using your ${pinned.length} chosen colour${pinned.length === 1 ? '' : 's'} first.`
            : photo
              ? 'Taken from your photo. Tap any skein below to override.'
              : preview.usedFallback
                ? 'Nothing recognised in your words yet — tap skeins to choose, or keep typing.'
                : preview.sourceNote}
        </p>
        <ul className="palette">
          {effectiveColors.map((code, i) => {
            const f = flossByCode(code);
            return (
              <li key={`${code}-${i}`}>
                <span className="swatch swatch--lg" style={{ background: f?.hex }} />
                <span className="swatch-label">
                  <strong>{code}</strong>
                  <em>{f?.name}</em>
                </span>
              </li>
            );
          })}
        </ul>

        <details>
          <summary>Pick exact skeins ({FLOSS.length} in the palette)</summary>
          <div className="skein-grid">
            {FLOSS.map((f) => {
              const on = pinned.includes(f.dmc);
              return (
                <button
                  key={f.dmc}
                  type="button"
                  className={on ? 'skein skein--on' : 'skein'}
                  style={{ background: f.hex, color: luminance(f.hex) > 140 ? '#222' : '#fff' }}
                  onClick={() => togglePinned(f.dmc)}
                  aria-pressed={on}
                  title={`DMC ${f.dmc} — ${f.name}`}
                >
                  {f.dmc}
                </button>
              );
            })}
          </div>
          {pinned.length > 0 && (
            <button type="button" className="link" onClick={() => setPinned([])}>
              Clear chosen colours
            </button>
          )}
        </details>
      </section>

      <section className="card">
        <h2>Kind of bracelet</h2>
        <div className="chips">
          {STYLE_CHOICES.map((c) => (
            <button
              key={c.id}
              type="button"
              className={style === c.id ? 'chip chip--on' : 'chip'}
              onClick={() => setStyle(c.id)}
              aria-pressed={style === c.id}
            >
              <strong>{c.label}</strong>
              <em>{c.hint}</em>
            </button>
          ))}
        </div>
      </section>

      <section className="card">
        <h2>Size</h2>
        <div className="chips chips--tight">
          {WRIST_PRESETS.map((w) => (
            <button
              key={w.id}
              type="button"
              className={Math.abs(sizing.wristCm - w.cm) < 0.01 ? 'chip chip--on' : 'chip'}
              onClick={() => setSizing((s) => ({ ...s, wristCm: w.cm }))}
            >
              <strong>{w.label}</strong>
              <em>
                {w.cm} cm / {w.inches}
              </em>
            </button>
          ))}
        </div>
        <label className="field field--inline">
          <span className="field-label">Or measure the wrist (cm)</span>
          <input
            type="number"
            min={9}
            max={24}
            step={0.5}
            value={sizing.wristCm}
            onChange={(e) => setSizing((s) => ({ ...s, wristCm: clamp(Number(e.target.value), 9, 24) }))}
          />
        </label>
        <div className="chips chips--tight">
          {EASE_PRESETS.map((e) => (
            <button
              key={e.id}
              type="button"
              className={Math.abs(sizing.easeCm - e.cm) < 0.01 ? 'chip chip--on' : 'chip'}
              onClick={() => setSizing((s) => ({ ...s, easeCm: e.cm }))}
            >
              <strong>{e.label}</strong>
              <em>+{e.cm} cm</em>
            </button>
          ))}
        </div>
      </section>

      <section className="card">
        <h2>Seed</h2>
        <p className="muted small">
          The seed decides which options you get. The same words and the same seed always give the
          same five designs, so you can send someone a link and know they will see what you saw.
        </p>
        <div className="row">
          <input
            className="seed-input"
            value={seed}
            onChange={(e) => setSeed(e.target.value.toUpperCase().slice(0, 12))}
            aria-label="Seed"
          />
          <button type="button" className="secondary" onClick={() => setSeed(randomSeed())}>
            New seed
          </button>
        </div>
      </section>

      {error && <p className="error">{error}</p>}

      <div className="sticky-actions">
        <button type="button" className="primary" disabled={Boolean(busy)} onClick={() => void go(useAi)}>
          {busy ?? 'Show me designs'}
        </button>
        {keyPresent && (
          <label className="ai-toggle">
            <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} />
            <span>Use Gemini to interpret my words</span>
          </label>
        )}
        {!keyPresent && (
          <p className="muted small centered">
            Working entirely offline. No API key set, so nothing leaves this device.{' '}
            <a href="#/settings">Add a key</a> if you want AI to help interpret your words.
          </p>
        )}
      </div>
    </div>
  );
}

function clamp(n: number, lo: number, hi: number): number {
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : lo;
}

function dedupe(codes: string[]): string[] {
  const out: string[] = [];
  for (const c of codes) if (flossByCode(c) && !out.includes(c)) out.push(c);
  return out;
}
